import React, { useState, useEffect } from 'react';
import { Menu, X, Search, Grid, ShoppingCart, User as UserIcon, Package, Sun, Moon } from 'lucide-react';
import { useCart } from '../CartContext';
import { useAuth } from '../AuthContext';
import AuthModal from './AuthModal';

interface NavbarProps {
  onOpenSearch: () => void;
  onOpenCollection: () => void;
  onOpenOrders: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onOpenSearch, onOpenCollection, onOpenOrders }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { cartItems, setIsCartOpen } = useCart();
  const { user, logout } = useAuth();
  const cartCount = cartItems.reduce(
  (sum, item) => sum + item.quantity,
  0
);

  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme') || 
      (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
  });

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prevTheme => (prevTheme === 'dark' ? 'light' : 'dark'));
  };


  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
   
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      
    };
  }, []);

  return (
    <>
      <nav className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-neutral-900/90 backdrop-blur-md py-4 border-b border-white/10' : 'bg-transparent py-6'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="text-2xl font-black tracking-tighter italic relative group cursor-pointer">
            <span className="inline-block" style={{ animation: 'logoGlow 3s ease-in-out infinite' }}>YUME</span><span className="text-amber-500 inline-block" style={{ animation: 'logoPulse 2.5s ease-in-out infinite' }}>CAV</span>
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 transition-all duration-300 group-hover:w-full"></span>
            <style>{`
              @keyframes logoPulse {
                0%, 100% { text-shadow: 0 0 0px rgba(168,85,247,0); }
                50% { text-shadow: 0 0 12px rgba(168,85,247,0.6), 0 0 24px rgba(168,85,247,0.3); }
              }
              @keyframes logoGlow {
                0%, 100% { text-shadow: none; }
                50% { text-shadow: 0 0 8px rgba(168,85,247,0.15); }
              }
            `}</style>
          </div>
          
          <div className="hidden md:flex space-x-8 text-sm font-medium tracking-widest uppercase items-center">
            {/* Added Explore Collection trigger */}
            <button onClick={onOpenCollection} className="hover:text-amber-400 transition-colors relative group flex items-center gap-2">
              <Grid className="w-4 h-4" />
              Explore
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 transition-all duration-300 group-hover:w-full"></span>
            </button>

            {['Drops', 'Data-Bank'].map((item) => (
              <a key={item} href={`#${item.toLowerCase().replace(' ', '-')}`} className="hover:text-amber-400 transition-colors relative group">
                {item}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-amber-500 transition-all duration-300 group-hover:w-full"></span>
              </a>
            ))}
            
            <div className="w-px h-6 bg-white/20 mx-2"></div>

            <button 
              onClick={toggleTheme}
              className="group hover:text-amber-500 transition-colors p-1"
              aria-label="Toggle Theme"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5 group-hover:scale-110 transition-transform" /> : <Moon className="w-5 h-5 group-hover:scale-110 transition-transform" />}
            </button>

            <button 
              onClick={onOpenSearch}
              className="group hover:text-amber-500 transition-colors p-1"
              aria-label="Search"
            >
              <Search className="w-5 h-5 group-hover:scale-110 transition-transform" />
            </button>

            {/* Auth Button */}
            <div className="relative">
              <button 
                onClick={() => user ? setIsUserMenuOpen(!isUserMenuOpen) : setIsAuthModalOpen(true)}
                className="group hover:text-amber-500 transition-colors p-1"
                aria-label="Account"
                title={user ? "Account" : "Login"}
              >
                <UserIcon className={`w-5 h-5 group-hover:scale-110 transition-transform ${user ? 'text-amber-500' : ''}`} />
              </button>

              {/* User Dropdown Menu */}
              {user && isUserMenuOpen && (
                <div className="absolute right-0 top-10 bg-neutral-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[160px] z-50">
                  <button 
                    onClick={() => { setIsUserMenuOpen(false); onOpenOrders(); }}
                    className="w-full px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors flex items-center gap-2"
                  >
                    <Package className="w-3.5 h-3.5 text-amber-500" /> My Orders
                  </button>
                  <div className="h-px bg-white/5" />
                  <button 
                    onClick={() => { setIsUserMenuOpen(false); logout(); }}
                    className="w-full px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors text-red-400"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>

            <button 
              onClick= {() => setIsCartOpen(true)}
              className="relative group hover:text-amber-500 transition-colors p-1 js-cart-icon"
              aria-label="Cart"
            >
              <ShoppingCart className={`w-5 h-5 ${cartCount > 0 ? 'fill-amber-500 text-amber-500' : ''} group-hover:scale-110 transition-transform`} />
              {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-white text-black text-[9px] font-bold flex items-center justify-center rounded-full">
              {cartCount}
              </span>
              )}
            </button>
          </div>

          <div className="flex items-center gap-6 md:hidden">
             <button onClick={toggleTheme} aria-label="Toggle Theme" className="text-white hover:text-amber-500 transition-colors">
               {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
             </button>
             
             <button onClick={onOpenSearch} aria-label="Search" className="text-white hover:text-amber-500 transition-colors">
               <Search className="w-5 h-5" />
             </button>
             
             {/* Auth Button Mobile */}
             <div className="relative">
               <button 
                  onClick={() => user ? setIsUserMenuOpen(!isUserMenuOpen) : setIsAuthModalOpen(true)}
                  className="text-white hover:text-amber-500 transition-colors"
                  aria-label={user ? "Account" : "Login"}
                  title={user ? "Account" : "Login"}
                >
                 <UserIcon className={`w-5 h-5 ${user ? 'text-amber-500' : ''}`} />
               </button>

               {/* User Dropdown Menu Mobile */}
               {user && isUserMenuOpen && (
                 <div className="absolute right-0 top-10 bg-neutral-900 border border-white/10 rounded-xl shadow-2xl overflow-hidden min-w-[160px] z-50">
                   <button 
                     onClick={() => { setIsUserMenuOpen(false); onOpenOrders(); }}
                     className="w-full px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors flex items-center gap-2"
                   >
                     <Package className="w-3.5 h-3.5 text-amber-500" /> My Orders
                   </button>
                   <div className="h-px bg-white/5" />
                   <button 
                     onClick={() => { setIsUserMenuOpen(false); logout(); }}
                     className="w-full px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest hover:bg-white/5 transition-colors text-red-400"
                   >
                     Logout
                   </button>
                 </div>
               )}
             </div>

             <button 
                onClick={() => setIsCartOpen(true)}
                aria-label="Cart"
                className="relative text-white hover:text-amber-500 transition-colors js-cart-icon"
              >
              <ShoppingCart className={`w-5 h-5 ${cartCount > 0 ? 'fill-amber-500 text-amber-500' : ''}`} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-white text-black text-[9px] font-bold flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </button>
            <button aria-label="Toggle Menu" className="text-white hover:text-amber-500 transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 bg-neutral-900 flex flex-col justify-center items-center space-y-8 animate-fade-in">
          <button onClick={() => { setIsMenuOpen(false); onOpenCollection(); }} className="text-2xl font-bold uppercase tracking-widest hover:text-amber-500 transition-colors flex items-center gap-3">
             <Grid className="w-6 h-6" /> Explore
          </button>
          {['Drops', 'Data-Bank'].map((item) => (
            <a key={item} href={`#${item.toLowerCase().replace(' ', '-')}`} onClick={() => setIsMenuOpen(false)} className="text-2xl font-bold uppercase tracking-widest hover:text-amber-500 transition-colors">
              {item}
            </a>
          ))}
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
    </>
  );
};

export default Navbar;